import { APIClient } from "../helpers/api_helper";
import * as url from "../helpers/url_helper";
import {
  JobItem,
  JobCreateRequest,
  JobUpdateRequest,
  JobsResponse,
} from "../interfaces/Job";
import { DEFAULT_PAGE_SIZE } from "../common/constants";

const api = new APIClient();

/**
 * Get all jobs with optional filters (paginated)
 * @param pageNumber - Page number (default: 1)
 * @param pageSize - Page size (default: DEFAULT_PAGE_SIZE)
 * @param clientId - Optional client filter (UUID)
 * @param status - Optional status filter (integer)
 * @param search - Optional search term (searches JobNumber and Title)
 * @param include - Optional include parameter (e.g., "workOrders,scaffolds,quotes")
 * @returns Promise with paginated jobs response
 */
export const getJobs = (
  pageNumber: number = 1,
  pageSize: number = DEFAULT_PAGE_SIZE,
  clientId?: string,
  status?: number,
  search?: string,
  include?: string
): Promise<JobsResponse> => {
  const params: Record<string, string | number> = {
    pageNumber,
    pageSize,
  };
  
  if (clientId) params.clientId = clientId;
  if (status !== undefined) params.status = status;
  if (search) params.search = search;
  if (include) params.include = include;
  
  return api.get<JobsResponse>(url.GET_JOBS, params);
};

/**
 * Get job by ID
 * @param id - Job ID (UUID)
 * @param include - Optional include parameter (e.g., "workOrders,scaffolds,quotes")
 * @returns Promise with job item (includes workOrders array when include=workOrders)
 */
export const getJobById = (id: string, include?: string): Promise<JobItem> => {
  const params: Record<string, string> = {};
  if (include) {
    params.include = include;
  }
  return api.get<JobItem>(`${url.GET_JOB_BY_ID}/${id}`, params);
};

export interface JobWithDetailParams {
  includeWorkorder?: boolean;
  includeScaffold?: boolean;
  includeQuote?: boolean;
  includeScaffoldTask?: boolean;
}

/**
 * Get job by ID with optional related entities (Job WithDetail API).
 * Use query params to include only what you need. Scaffold tasks not requested by default.
 * @param id - Job ID (UUID)
 * @param params - includeWorkorder, includeScaffold, includeQuote, includeScaffoldTask (default false)
 */
export const getJobWithDetail = (
  id: string,
  params: JobWithDetailParams = {}
): Promise<JobItem> => {
  const query: Record<string, boolean> = {};
  if (params.includeWorkorder !== undefined) query.includeWorkorder = params.includeWorkorder;
  if (params.includeScaffold !== undefined) query.includeScaffold = params.includeScaffold;
  if (params.includeQuote !== undefined) query.includeQuote = params.includeQuote;
  if (params.includeScaffoldTask !== undefined) query.includeScaffoldTask = params.includeScaffoldTask;
  return api.get<JobItem>(`${url.GET_JOB_BY_ID}/${id}/WithDetail`, query);
};

/**
 * Create a new job
 * @param payload - Job create request payload
 * @returns Promise with created job item
 */
export const createJob = (payload: JobCreateRequest): Promise<JobItem> => {
  return api.create<JobItem>(url.CREATE_JOB, payload);
};

/**
 * Update an existing job (Title/Description/PrimarySiteId only)
 * @param id - Job ID (UUID)
 * @param payload - Job update request payload
 * @returns Promise with updated job item
 */
export const updateJob = (
  id: string,
  payload: JobUpdateRequest
): Promise<JobItem> => {
  return api.put<JobItem>(`${url.UPDATE_JOB}/${id}`, payload);
};

/**
 * Get jobs by client ID (paginated)
 * @param clientId - Client ID (UUID)
 * @param pageNumber - Page number (default: 1)
 * @param pageSize - Page size (default: DEFAULT_PAGE_SIZE)
 * @returns Promise with paginated jobs response
 */
export const getJobsByClient = (
  clientId: string,
  pageNumber: number = 1,
  pageSize: number = DEFAULT_PAGE_SIZE
): Promise<JobsResponse> => {
  return api.get<JobsResponse>(`${url.GET_JOBS_BY_CLIENT}/${clientId}`, {
    pageNumber,
    pageSize,
  });
};

/**
 * Get jobs by site ID (paginated)
 * @param siteId - Site ID (UUID)
 * @param pageNumber - Page number (default: 1)
 * @param pageSize - Page size (default: DEFAULT_PAGE_SIZE)
 * @returns Promise with paginated jobs response
 */
export const getJobsBySite = (
  siteId: string,
  pageNumber: number = 1,
  pageSize: number = DEFAULT_PAGE_SIZE
): Promise<JobsResponse> => {
  return api.get<JobsResponse>(`${url.GET_JOBS_BY_SITE}/${siteId}`, {
    pageNumber,
    pageSize,
  });
};

/**
 * Get job by quote ID (1:1 relationship)
 * @param quoteId - Quote ID (UUID)
 * @returns Promise with job item
 */
export const getJobByQuoteId = (quoteId: string): Promise<JobItem> => {
  return api.get<JobItem>(`${url.GET_JOB_BY_QUOTE_ID}/${quoteId}`);
};

/**
 * Auto-generate a job number
 * @returns Promise with generated job number
 */
export const autogenerateJobNumber = (): Promise<{ isSuccess: boolean; message: string; generatedCode: string }> => {
  return api.create<{ isSuccess: boolean; message: string; generatedCode: string }>(url.AUTOGENERATE_JOB_NUMBER, {});
};
